import { useState, useEffect, useMemo } from 'react';
import { useRaces } from '@/hooks/useRaces';
import { useRaceRegistrations } from '@/hooks/useRegistrations';
import { useRaceTypes } from '@/hooks/useRaceTypes';
import { useBibPickupActions } from './useBibPickupActions';
import BibPickupManagerView from './bibpickupmanager.view';

interface BibPickupManagerOwnProps {
  raceId?: string;
}

const BibPickupManager: React.FC<BibPickupManagerOwnProps> = ({ raceId: raceIdProp }) => {
  const isEmbedded = !!raceIdProp;
  const { data: races = [] } = useRaces();
  const [selectedRaceIdState, setSelectedRaceIdState] = useState('');
  const selectedRaceId = isEmbedded ? raceIdProp : selectedRaceIdState;
  const [searchQuery, setSearchQuery] = useState('');

  const actions = useBibPickupActions(setSearchQuery);

  useEffect(() => {
    if (!isEmbedded && races.length > 0 && !selectedRaceIdState) {
      setSelectedRaceIdState(races[0].id);
    }
  }, [races, selectedRaceIdState, isEmbedded]);

  const { data: rawRegistrations = [], isLoading: loading } = useRaceRegistrations(selectedRaceId);
  const { data: rawRaceTypes = [] } = useRaceTypes(selectedRaceId);

  const registrations = useMemo(
    () => rawRegistrations.filter((r: any) => r.registrationStatus === 'Confirmed' && r.bibNumber),
    [rawRegistrations]
  );

  const raceTypes = useMemo(() => {
    const map: Record<string, string> = {};
    rawRaceTypes.forEach((t: any) => { map[t.id] = t.name; });
    return map;
  }, [rawRaceTypes]);

  const filteredRegistrations = registrations.filter((reg: any) => {
    if (!searchQuery.trim()) return false;
    const q = searchQuery.toLowerCase();
    return (
      reg.firstName.toLowerCase().includes(q) ||
      reg.lastName.toLowerCase().includes(q) ||
      `${reg.firstName} ${reg.lastName}`.toLowerCase().includes(q) ||
      (reg.bibNumber && reg.bibNumber.toString().includes(q))
    );
  });

  const pickedUpCount = registrations.filter((r: any) => r.bibPickedUp).length;
  const totalCount = registrations.length;

  return (
    <BibPickupManagerView
      races={isEmbedded ? [] : races}
      selectedRaceId={selectedRaceId}
      setSelectedRaceId={setSelectedRaceIdState}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      searchInputRef={actions.searchInputRef}
      loading={loading}
      filteredRegistrations={filteredRegistrations}
      raceTypes={raceTypes}
      pickedUpCount={pickedUpCount}
      totalCount={totalCount}
      handleBibPickup={actions.handleBibPickup}
      isPending={actions.isPending}
      embedded={isEmbedded}
    />
  );
};

export default BibPickupManager;
